<template>
  <div style="width: 100%; height: 100%;">
    <template v-if="isLoading">
      <v-row no-gutters class="fill-height">
        <v-col cols="12">
          <main-loader />
        </v-col>
      </v-row>
    </template>
    <template v-else>
      <div style="width: 100%; gap: 2rem" class="pa-6 d-flex flex-column">
        <span class="ml-auto">
          <v-subheader class="delete-site-button text-caption pa-0 error--text" style="height: auto;">
            <a
              class="error--text d-flex align-center"
              @click="isDeleteDialogLoaded = isDeleteDialogVisible = true"
              style="gap: 0.2rem;"
            >
              <v-icon color="error" small>
                mdi-delete
              </v-icon>
              <span>
                {{ $i18n.t('table.rziMonitoring.deleteSite') }}
              </span>
            </a>
          </v-subheader>
        </span>
        <v-expansion-panels accordion focusable>
          <v-expansion-panel>
            <v-expansion-panel-header>{{ $i18n.t('table.rziMonitoring.general') }}</v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-row no-gutters class="pr-5 mt-4">
                <v-spacer></v-spacer>
                <v-col cols="auto">
                  <v-row no-gutters>
                    <v-col v-if="!isEditingOn">
                      <base-rounded-button
                        @click="isEditingOn = true"
                        color="rgba(235, 235, 235, 0.5)"
                        size="x-small"
                        icon="mdi-pencil"
                        icon-color="#202124"
                      />
                    </v-col>
                    <template v-else>
                      <v-col>
                        <base-rounded-button
                          @click="handleEdit"
                          color="success"
                          size="x-small"
                          :loading="isEditLoading"
                          icon="mdi-check"
                          icon-color="white"
                        />
                      </v-col>
                      <v-col>
                        <base-rounded-button
                          @click="cancelEditing"
                          color="error"
                          :disabled="isEditLoading"
                          size="x-small"
                          icon="mdi-close"
                          icon-color="white"
                        />
                      </v-col>
                    </template>
                  </v-row>
                </v-col>
              </v-row>
              <feature-card-data
                class="custom-data ml-5"
                :elements="formSchema"
                v-model="values"
                :is-editing-on="isEditingOn"
                disableWidthLimit
                dynamic-icon-column
              />
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
        <v-row no-gutters class="d-flex align-center action-input-row" style="gap: 1rem">
          <v-col cols="4">
            <data-input v-bind="monitoringYearsInput" v-model="currentMonitoringYear" />
          </v-col>
          <v-col cols="auto">
            <base-button-subheader
              @click="isAddMonitoringYearDialogLoaded = isAddMonitoringYearDialogVisible = true"
              :straight-text="$i18n.t('table.rziMonitoring.addMonitoringYear')"
            />
          </v-col>
          <v-col v-if="currentMonitoringYearData" cols="auto">
            <base-button-subheader
              cancel
              @click="handleGenerateReport"
              :loading="isGenerateReportButtonLoading"
              :straight-text="$i18n.t('table.rziMonitoring.generateReport')"
            />
          </v-col>
        </v-row>
        <v-row no-gutters>
          <v-col>
            <rzi-monitoring-year-section
              ref="monitoring-year-section"
              v-if="currentMonitoringYearData"
              :key="currentMonitoringYear"
              :main-item="item"
              @refreshMonitoringYears="refreshMonitoringYears"
              :current-metadata="currentMetadata"
              :item="currentMonitoringYearData"
              :is-drawing-on.sync="isDrawingOn"
              :sticky-buttons.sync="stickyButtons"
            />
          </v-col>
        </v-row>
      </div>
      <v-row
        v-if="isDrawingOn && stickyButtons.length"
        class="py-5 px-2 sticky-buttons d-flex"
        style="gap: 1rem"
        no-gutters
      >
        <v-col cols="auto" class="d-flex">
          <div
            v-for="(button, idx) of stickyButtons.filter(btn =>
              ['addMultipart', 'deleteMultipart'].includes(btn.value)
            )"
            :key="idx"
          >
            <base-rounded-button
              :icon="button.icon"
              size="x-small"
              :disabled="button.disabled"
              :is-active="button.active"
              v-bind="getButtonStyle(button.active)"
              :tooltip="button.tooltip"
              @click="button.action"
            />
          </div>
        </v-col>

        <v-col cols="auto" class="d-flex">
          <span
            v-for="(button, idx) of stickyButtons.filter(
              btn => !['addMultipart', 'deleteMultipart'].includes(btn.value)
            )"
            :key="idx"
          >
            <div>
              <base-button-subheader
                v-bind="{ ...button, cancel: button.cancel || button.cancelLike }"
                @click="button.action"
              />
            </div>
          </span>
        </v-col>
      </v-row>
    </template>
    <main-input-dialog
      v-if="isAddMonitoringYearDialogLoaded"
      :is-visible.sync="isAddMonitoringYearDialogVisible"
      :loading="isAddMonitoringYearDialogLoading"
      :title-translation="$i18n.t('table.rziMonitoring.addYearMonitoringModal')"
      dialog-width="30%"
      agree-text="dialog.agreeAdd"
      :inputs="addMonitoringYearInputs"
      @agree="addMonitoringYear"
    >
      <template #[`dataSourceName.input`]="data">
        <v-col cols="6">
          <data-input v-model="newMonitoringYear" v-bind="data" />
        </v-col>
      </template>
    </main-input-dialog>
    <main-dialog
      :v-if="isDeleteDialogLoaded"
      :is-visible.sync="isDeleteDialogVisible"
      :loading="isDeleteDialogLoading"
      :title="$i18n.t('table.rziMonitoring.deleteMonitoring')"
      :body="$i18n.t('table.rziMonitoring.deleteMonitoringBody')"
      @agree="handleDelete"
      agree-text="dialog.agreeDelete"
    />
  </div>
</template>

<script>
import MainLoader from '@/components/MainLoader';
import FeatureCardData from '@/components/FeatureCardData';
import MainDialog from '@/components/MainDialog';
import DataInput from '@/components/DataInput';
import RziMonitoringYearSection from '@/components/RziMonitoringYearSection';
import MainInputDialog from '@/components/MainInputDialog';

import { call, get } from 'vuex-pathify';
import prepareAttributesFeatureCardMixin from '@/mixins/prepareAttributesFeatureCardMixin';
import { systemAttributes } from '@/assets/js/variables';

export default {
  name: 'RziMonitoringItemSection',
  mixins: [prepareAttributesFeatureCardMixin],
  components: {
    MainDialog,
    MainLoader,
    DataInput,
    FeatureCardData,
    MainInputDialog,
    RziMonitoringYearSection,
  },
  props: {
    currentMetadata: {
      type: Object,
    },
    item: {
      type: Object,
      required: true,
    },
  },
  watch: {
    item: {
      immediate: true,
      handler(nV, oV) {
        if (nV?.id !== oV?.id) this.init();
      },
    },
  },
  data: () => ({
    isDeleteDialogLoaded: false,
    isDeleteDialogVisible: false,
    isDeleteDialogLoading: false,
    values: {},
    formSchema: [],
    isLoading: true,
    isEditingOn: false,
    isEditLoading: false,
    monitoringYearsFeatures: [],
    currentMonitoringYear: null,
    newMonitoringYear: null,
    isAddMonitoringYearDialogLoaded: false,
    isAddMonitoringYearDialogVisible: false,
    isAddMonitoringYearDialogLoading: false,
    stickyButtons: [],
    isDrawingOn: false,
    isGenerateReportButtonLoading: false,
  }),
  computed: {
    layersMetadata: get('layers/metadata'),
    addMonitoringYearInputs() {
      return [
        {
          name: 'dataSourceName',
          translationPath: 'table.rziMonitoring.monitoringYear',
          dataType: {
            name: 'select',
          },
          items: Array(this.currentMetadata.monitoringYears.maxYear + 1 - this.currentMetadata.monitoringYears.minYear)
            .fill(this.currentMetadata.monitoringYears.minYear)
            .map((year, index) => year + index)
            .filter(
              year =>
                !this.monitoringYearsFeatures.find(
                  item => item.properties[this.currentMetadata.monitoringYears.attributes.year] === year
                )
            ),
          cols: '12',
          rules: [v => !!v || v == '0' || 'rules.required'],
        },
      ];
    },
    currentMonitoringYearData() {
      return this.monitoringYearsFeatures.find(item => item.id === this.currentMonitoringYear) ?? null;
    },
    monitoringYearsInput() {
      return {
        dataType: {
          name: 'select',
        },
        disabled: !this.monitoringYearsFeatures.length,
        hideDetails: true,
        label: this.$i18n.t('table.rziMonitoring.monitoringYear'),
        items: this.monitoringYearsFeatures.map(item => {
          return {
            value: item.id,
            text: item.properties[this.currentMetadata.monitoringYears.attributes.year],
          };
        }),
      };
    },
  },
  methods: {
    editNonTopoFeatureAction: call('layers/editDatasourceFeature'),
    deleteDatasourceFeature: call('layers/deleteDatasourceFeature'),
    getDataSourceFeatures: call('layers/getDataSourceFeatures'),
    addNonTopoFeatureAction: call('layers/addDatasourceFeature'),
    generateMonitoringReport: call('rziData/generateMonitoringReport'),
    cancelEditing() {
      this.values = JSON.parse(JSON.stringify(this.valuesCopy));
      this.isEditingOn = false;
    },
    getButtonStyle(isActive) {
      return isActive
        ? { color: this.$_colors.primarybackground, iconColor: this.$_colors.primary }
        : {
            color: 'rgba(225,225,225,1)',
            iconColor: 'rgba(0,0,0,0.7)',
          };
    },
    async handleGenerateReport() {
      this.isGenerateReportButtonLoading = true;
      try {
        await this.generateMonitoringReport(this.currentMonitoringYear);
      } catch (error) {
        console.error(error);
      } finally {
        this.isGenerateReportButtonLoading = false;
      }
    },
    async addMonitoringYear() {
      this.isAddMonitoringYearDialogLoading = true;
      try {
        const body = {
          ...this.currentMetadata.monitoringYears.statics.reduce((acc, item) => {
            acc[item.attribute] = item.value;
            return acc;
          }, {}),
          [this.currentMetadata.monitoringYears.attributes.year]: this.newMonitoringYear,
          [this.currentMetadata.monitoringYears.attributes.site]: this.item.id,
        };
        await this.addNonTopoFeatureAction({
          datasource: this.currentMetadata.monitoringYears.datasource,
          body: body,
        });
        await this.setMonitoringYearsFeatures();
        this.isAddMonitoringYearDialogVisible = this.isAddMonitoringYearDialogLoading = false;
        this.currentMonitoringYear =
          this.monitoringYearsFeatures.find(
            ft => ft.properties[this.currentMetadata.monitoringYears.attributes.year] === this.newMonitoringYear
          )?.id ?? null;
        this.newMonitoringYear = null;
      } catch {
        this.isAddMonitoringYearDialogLoading = false;
      }
    },
    async handleEdit() {
      this.isEditLoading = true;
      try {
        await this.editNonTopoFeatureAction({
          datasource: this.currentMetadata.protectedSites.datasource,
          id: this.item.id,
          body: this.values,
        });
        this.$emit('itemEdited', () => {
          this.isEditLoading = false;
          this.isEditingOn = false;
        });
      } catch (error) {
        console.log(error);
        this.isEditLoading = false;
      }
    },
    async handleDelete() {
      this.isDeleteDialogLoading = true;
      try {
        await this.deleteDatasourceFeature({
          datasource: this.currentMetadata.protectedSites.datasource,
          id: this.item.id,
          params: {
            cascade: true,
          },
        });
        this.$emit('deleteSite', () => {
          this.isDeleteDialogVisible = this.isDeleteDialogLoading = false;
        });
      } catch {
        this.isDeleteDialogLoading = false;
      }
    },
    async refreshMonitoringYears(closeDialog) {
      await this.setMonitoringYearsFeatures();
      closeDialog();
    },
    async setMonitoringYearsFeatures() {
      const [monitoringYears] = await Promise.all([
        this.getDataSourceFeatures({
          dataSource: this.currentMetadata.monitoringYears.datasource,
          filters: {
            $EQUAL: {
              [`${this.currentMetadata.monitoringYears.datasource}.${this.currentMetadata.monitoringYears.attributes.site}`]: {
                value: this.item.feature.id,
              },
            },
          },
        }),
      ]);
      this.monitoringYearsFeatures = monitoringYears.features;
    },
    async init() {
      this.isLoading = true;
      try {
        this.formSchema = this.getForm(
          null,
          this.layersMetadata[this.currentMetadata.protectedSites.datasource].attributes_schema.attributes,
          {
            filteredOutAttributes: this.layersMetadata[
              this.currentMetadata.protectedSites.datasource
            ].attributes_schema.attributes
              .filter(attr => systemAttributes.includes(attr.name))
              .map(attr => attr.name),
          }
        ).map(attr => {
          return {
            ...attr,
            ...([this.currentMetadata.protectedSites.attributes.subtitle].includes(attr.attribute) && {
              editable: false,
            }),
          };
        });
        const feature = this.item.feature;
        const idAttributeName = this.layersMetadata[this.currentMetadata.protectedSites.datasource].attributes_schema
          .id_name;
        const properties = this.getFlattenProperties(JSON.parse(JSON.stringify(feature)), idAttributeName);
        this.values = this.getValues(this.formSchema, properties);
        this.valuesCopy = JSON.parse(JSON.stringify(this.values));

        await this.setMonitoringYearsFeatures();
      } catch (error) {
        console.log(error);
      } finally {
        this.isLoading = false;
      }
    },
  },
  mounted() {},
};
</script>

<style lang="scss" scoped>
.action-input-row :deep(.base-button) {
  margin-right: 0 !important;
}

.delete-site-button:hover span {
  text-decoration: underline;
}
.sticky-buttons {
  background-color: white;
  z-index: 1;
  box-shadow: 0px -3px 6px rgba(0, 0, 0, 0.15);

  width: 100%;
  position: sticky;
  bottom: 0px;
}
</style>
